import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation } from '@tanstack/react-query'
import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { ArrowLeft, CreditCard, Info, Banknote, AlertCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { getInvoices1Options, preparePaymentMutation, createPaymentSessionMutation } from '@/lib/client/@tanstack/react-query.gen'

export const Route = createFileRoute('/link/$parentId/pay')({
  component: PaymentPage,
})

function PaymentPage() {
  const { parentId } = Route.useParams()
  const navigate = useNavigate()
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([])
  const [customAmount, setCustomAmount] = useState('')
  const [useCustomAmount, setUseCustomAmount] = useState(false)
  const [showHelloAssoModal, setShowHelloAssoModal] = useState(false)

  const { data: invoices } = useQuery({
    ...getInvoices1Options({
      path: { parentId },
    }),
  })

  const outstandingInvoices = invoices?.filter(
    (inv: any) => inv.status === 'PENDING' || inv.status === 'PARTIALLY_PAID'
  ) || []

  const previewMutation = useMutation({
    ...preparePaymentMutation(),
  })

  const preview = previewMutation.data
  const isLoadingPreview = previewMutation.isPending

  React.useEffect(() => {
    if ((selectedInvoices.length > 0 && !useCustomAmount) || (useCustomAmount && customAmount !== '')) {
      previewMutation.mutate({
        path: { parentId },
        body: {
          invoiceIds: useCustomAmount ? undefined : selectedInvoices,
          customAmount: useCustomAmount && customAmount ? parseFloat(customAmount) : undefined,
        },
      })
    }
  }, [parentId, selectedInvoices, customAmount, useCustomAmount])

  const paymentMutation = useMutation({
    ...createPaymentSessionMutation(),
    onSuccess: (data: any) => {
      window.location.href = data.checkoutUrl
    },
  })

  const handleToggleInvoice = (invoiceId: string) => {
    setSelectedInvoices(prev =>
      prev.includes(invoiceId)
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    )
  }

  const handleSelectAll = () => {
    if (selectedInvoices.length === outstandingInvoices.length) {
      setSelectedInvoices([])
    } else {
      setSelectedInvoices(outstandingInvoices.map((inv: any) => inv.id))
    }
  }

  const canPay = (selectedInvoices.length > 0 && !useCustomAmount) || (useCustomAmount && customAmount && parseFloat(customAmount) > 0)

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/link/$parentId" params={{ parentId }}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au tableau de bord
            </Link>
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Régulariser mes factures</h1>
          <p className="text-gray-600 mt-2">Sélectionnez les factures à payer ou entrez un montant personnalisé</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Factures à régulariser</CardTitle>
                    <CardDescription>{outstandingInvoices.length} facture(s) en attente</CardDescription>
                  </div>
                  {outstandingInvoices.length > 0 && (
                    <Button variant="outline" size="sm" onClick={handleSelectAll}>
                      {selectedInvoices.length === outstandingInvoices.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {outstandingInvoices.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Aucune facture à régulariser</p>
                ) : (
                  <div className="space-y-3">
                    {outstandingInvoices.map((invoice: any) => (
                      <div
                        key={invoice.id}
                        className={`flex items-center space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedInvoices.includes(invoice.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                        } ${useCustomAmount ? 'opacity-50 pointer-events-none' : ''}`}
                        onClick={() => !useCustomAmount && handleToggleInvoice(invoice.id)}
                      >
                        <Checkbox
                          checked={selectedInvoices.includes(invoice.id)}
                          disabled={useCustomAmount}
                          onCheckedChange={() => handleToggleInvoice(invoice.id)}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{invoice.invoiceNumber}</p>
                              <p className="text-sm text-gray-600">
                                Période : {new Date(invoice.billingPeriodStart).toLocaleDateString('fr-FR')} au{' '}
                                {new Date(invoice.billingPeriodEnd).toLocaleDateString('fr-FR')}
                              </p>
                              <p className="text-sm text-gray-500">
                                Échéance : {new Date(invoice.dueDate).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold">{invoice.remainingAmount.toFixed(2)}€</p>
                              <p className="text-xs text-gray-500">
                                sur {invoice.totalAmount.toFixed(2)}€
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Montant personnalisé</CardTitle>
                <CardDescription>Entrez un montant spécifique à payer</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="custom-amount"
                      checked={useCustomAmount}
                      onCheckedChange={(checked) => {
                        setUseCustomAmount(!!checked)
                        if (checked) {
                          setSelectedInvoices([])
                        } else {
                          setCustomAmount('')
                        }
                      }}
                    />
                    <Label htmlFor="custom-amount" className="cursor-pointer">
                      Utiliser un montant personnalisé
                    </Label>
                  </div>

                  {useCustomAmount && (
                    <>
                      <div>
                        <Label htmlFor="amount">Montant (€)</Label>
                        <Input
                          id="amount"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                        />
                      </div>

                      <Alert>
                        <Info className="h-4 w-4" />
                        <AlertDescription className="text-sm">
                          Le montant sera automatiquement réparti sur vos factures par ordre d'ancienneté.
                        </AlertDescription>
                      </Alert>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Récapitulatif</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {preview && !isLoadingPreview ? (
                  <>
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Répartition du paiement :</p>
                      <div className="space-y-2">
                        {preview.allocations?.map((alloc: any) => (
                          <div key={alloc.invoiceId} className="flex justify-between text-sm">
                            <span className="text-gray-700">{alloc.invoiceNumber}</span>
                            <span className="font-medium">{alloc.allocatedAmount.toFixed(2)}€</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">Total à payer</span>
                      <span className="text-2xl font-bold text-blue-600">
                        {preview.totalAmount.toFixed(2)}€
                      </span>
                    </div>

                    <Button
                      className="w-full"
                      size="lg"
                      disabled={!canPay || paymentMutation.isPending}
                      onClick={() => setShowHelloAssoModal(true)}
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      Payer par CB
                    </Button>
                  </>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    {isLoadingPreview ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    ) : (
                      <p className="text-sm">Sélectionnez des factures ou entrez un montant pour voir le récapitulatif</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <Card className="mt-8 border-blue-200 bg-blue-50/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Banknote className="h-5 w-5 text-blue-600" />
              <CardTitle>Paiement par virement bancaire</CardTitle>
            </div>
            <CardDescription>
              Vous pouvez également régler vos factures par virement bancaire
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-900">
                <p><strong className="font-semibold">IMPORTANT :</strong> Vous devez <strong>ABSOLUMENT</strong> indiquer le(s) numéro(s) de facture dans le libellé du virement.
                <br />
                <strong>Sans le numéro de facture, nous ne pourrons pas identifier votre paiement.</strong></p>
              </AlertDescription>
            </Alert>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-600">Bénéficiaire</p>
                  <p className="font-semibold">Revacours</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-600">IBAN</p>
                  <p className="font-mono text-sm font-semibold">FR86 3000 2014 4300 0007 2543 N11</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-600">BIC</p>
                  <p className="font-mono font-semibold">CRLYFRPP</p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-600">Domiciliation</p>
                  <p className="text-sm">CL ATHISMONS (01443)</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-600">Code banque</p>
                  <p className="text-sm font-mono">30002 - Indicatif : 01443</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-600">N° de compte</p>
                  <p className="text-sm font-mono">0000072543N - Clé : 11</p>
                </div>
              </div>
            </div>

            <Separator />


              {selectedInvoices.length == 0 && <>
              <p>Merci de selectionner la ou les facture(s) que vous souhaitez régulariser</p></>}
{selectedInvoices.length > 0 && <div className="bg-white border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">
                📋 Libellé du virement
              </p>
              <p className="text-sm text-gray-700 mb-3">
                Indiquez <strong>OBLIGATOIREMENT</strong> le(s) numéro(s) de facture que vous souhaitez régler :
              </p>
              {selectedInvoices.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <p className="text-xs text-gray-600 mb-1">Factures sélectionnées :</p>
                  <p className="font-mono text-sm font-semibold text-blue-600">
                    {outstandingInvoices
                      .filter((inv: any) => selectedInvoices.includes(inv.id))
                      .map((inv: any) => inv.invoiceNumber.split("-")[inv.invoiceNumber.split("-").length - 1])
                      .join(', ')}
                  </p>
                </div>
              )}
              <p className="text-xs text-amber-700 mt-3 font-medium">
                ⚠️ Rappel : Sans le numéro de facture dans le libellé, votre paiement ne pourra pas être traité.
              </p>
            </div>}
            
          </CardContent>
        </Card>
      </div>

      <Dialog open={showHelloAssoModal} onOpenChange={setShowHelloAssoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Information HelloAsso</DialogTitle>
            <DialogDescription>
              Avant de continuer vers le paiement
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <p className="font-medium mb-2">HelloAsso pré-remplit un champ "Votre contribution au modèle solidaire de HelloAsso"</p>
                <p className="text-sm">
                  Il est possible de modifier cette contribution volontaire en cliquant sur <strong>"Modifier la contribution volontaire"</strong> sur la page HelloAsso.
                </p>
                <p className="text-sm mt-2">
                  Vous pouvez la mettre à zéro si vous le souhaitez.
                </p>
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHelloAssoModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={() => {
                setShowHelloAssoModal(false)
                paymentMutation.mutate({
                  path: { parentId },
                  body: {
                    invoiceIds: useCustomAmount ? undefined : selectedInvoices,
                    amount: useCustomAmount && customAmount ? parseFloat(customAmount) : undefined,
                  },
                })
              }}
              disabled={paymentMutation.isPending}
            >
              {paymentMutation.isPending ? 'Chargement...' : 'Continuer vers HelloAsso'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
